import { HOT } from "@hot-wallet/sdk";
import WebApp from "@twa-dev/sdk";
import { t } from "i18next";
import useSWR, { mutate } from "swr";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID } from "@/constants";
import { API_ROUTES, UserResponseData } from "@/interfaces/api";
import { APP_ROUTES } from "@/routes/constants";
import { hapiApi } from "@/services/api/hapi";
import { ApiVersion, apiVersion } from "@/utils";

import { useStore } from "../store";
import { UploadType } from "../store/useStore";
import { clearCache } from "../useNetworkDependentActions";

export const useUserLoading = () => {
  const {
    nearAccount,
    uploadType,
    evmAccount,
    solanaAccount,
    tonAccount,
    stellarAccount,
    updateUser,
    updatePreload,
    eraseUserData,
    network,
    updateUploadType,
    updateUserLoading,
  } = useStore((store) => ({
    nearAccount: store.user.nearAccount,
    evmAccount: store.user.evmAccount,
    solanaAccount: store.user.solanaAccount,
    tonAccount: store.user.tonAccount,
    stellarAccount: store.user.stellarAccount,
    updateUser: store.updateUser,
    updatePreload: store.updatePreload,
    eraseUserData: store.eraseUserData,
    network: store.network,
    uploadType: store.uploadType,
    updateUploadType: store.updateUploadType,
    updateUserLoading: store.updateUserLoading,
  }));
  const init = WebApp.initDataUnsafe;

  const address = `${nearAccount}`;

  return useSWR<UserResponseData | undefined>(
    `${API_ROUTES.USER}/${address}`,
    async () => {
      updateUserLoading(true);
      if (
        (location.pathname.includes(APP_ROUTES.AIRDROPS) || location.pathname.includes(APP_ROUTES.TOTAL_TRUST_SCORE)) &&
        uploadType === UploadType.Offchain
      ) {
        return;
      }

      updateUploadType(UploadType.Offchain);

      ToastService.loading({ text: t("Messages.LoadingUser"), id: TOAST_ID.USER_DATA });
      const hotConnection = await HOT.connection;

      const request = await hapiApi.getUser({
        nearAccount,
        evmAccount,
        solanaAccount,
        tonAccount,
        stellarAccount,
        tg: hotConnection?.telegramId || init.user?.id,
      });

      console.log("request", request);
      if ("message" in request && request.errorCode === 1) {
        if (request.message.includes("No jwtToken provided")) return;
        if (request.message.includes("Authentification error")) {
          eraseUserData();
          mutate(`${API_ROUTES.TRUST}/${network}/${apiVersion === ApiVersion.v1 ? nearAccount : tonAccount}`);
          clearCache();
          return;
        } else {
          throw Error(request.message);
        }
      }

      const returnObj = {
        ...request, //TODO: nft1 -> nft
        // nfts: user.data.nfts || [],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nfts: (request as any).nfts1,
      };

      return returnObj;
    },
    {
      revalidateOnFocus: false,
      errorRetryInterval: 2000,
      errorRetryCount: 3,
      onSuccess(data) {
        ToastService.clear(TOAST_ID.USER_DATA);
        if (!data) return;
        updateUser(data);
        updatePreload(false);
        updateUserLoading(false);
      },
      onError(err) {
        ToastService.update({ id: TOAST_ID.USER_DATA, text: err.message });
        hapiApi.logError({ nearAddress: nearAccount, action: "USER", error: err });
        console.error("Error: while loading user data \n\n", err);
        updateUserLoading(false);
      },
    },
  );
};
